import { StructuredDataService } from "@brookbush/structured-data";

import type { GetStaticProps } from "next";

import { AboutAccreditations, AboutHero, AboutTeam } from "./components";
import AboutPartners from "./components/AboutPartners";
import AboutResources from "./components/AboutResources";
import useScrollToHash from "~/hooks/useScrollToHash";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const AboutPage: NextPageWithProps<{
	structuredData: string;
}> = ({ structuredData }) => {
	useScrollToHash();

	return (
		<>
			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>
			<main aria-label="about-page">
				<AboutHero />
				<AboutPartners />
				<AboutAccreditations />
				<AboutResources />
				<AboutTeam />
			</main>
		</>
	);
};

export const getStaticProps: GetStaticProps = async () => {
	const promiseResult = await Promise.all([
		fetcher("/company-info/about"),
		fetcher("/courses/accreditations/by-profession"),
		fetcher("/company-info/partners"),
		fetcher("/company-info/customers"),
		fetcher("/company-info/team"),
		StructuredDataService.getOrganization({ include: ["*"] }),
		StructuredDataService.getBreadcrumbs("/about"),
	]);

	for (const result of promiseResult) {
		if (!result) {
			return {
				notFound: true,
			};
		}
	}

	const [
		about,
		accreditations,
		partners,
		customers,
		team,
		organizationSD,
		breadcrumbSD,
	] = promiseResult;

	return {
		props: {
			fallback: {
				"/company-info/about": about,
				"/courses/accreditations/by-profession": accreditations,
				"/company-info/partners": partners,
				"/company-info/customers": customers,
				"/company-info/team": team,
			},
			structuredData: StructuredDataService.withContext({
				"@graph": [organizationSD, breadcrumbSD],
			}),
		},
	};
};

export default AboutPage;
